import { supabase } from './supabase'
import { captureException } from './errorTracking'
import { secureRPCWithSchema } from './sqlInjectionProtection'

/**
 * GDPR Compliance Service
 * Handles GDPR-specific compliance features
 */

/**
 * Verify GDPR deletion
 */
export const verifyGDPRDeletion = async (organizationId, clientId, requestId = null) => {
  try {
    const verificationId = await secureRPCWithSchema('verify_gdpr_deletion', {
      org_id: organizationId,
      client_id_param: clientId,
      request_id: requestId,
    })

    // Fetch verification result
    const { data: verification, error: fetchError } = await supabase
      .from('gdpr_deletion_verification')
      .select('*')
      .eq('id', verificationId)
      .single()

    if (fetchError) throw fetchError

    return verification
  } catch (error) {
    console.error('Error verifying GDPR deletion:', error)
    captureException(error, { context: 'verify_gdpr_deletion' })
    throw error
  }
}

/**
 * Get GDPR compliance status
 */
export const getGDPRComplianceStatus = async (organizationId) => {
  try {
    // Get pending deletion requests
    const { data: pendingDeletions } = await supabase
      .from('gdpr_deletion_verification')
      .select('*')
      .eq('organization_id', organizationId)
      .in('verification_status', ['pending', 'in_progress', 'failed'])

    // Get recent data subject requests
    const { data: recentRequests } = await supabase
      .from('data_subject_requests')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false })
      .limit(10)

    // Get consent records
    const { data: consentRecords } = await supabase
      .from('consent_records')
      .select('consent_status, consent_type')
      .eq('organization_id', organizationId)

    const status = {
      pendingDeletions: pendingDeletions?.length || 0,
      failedDeletions: pendingDeletions?.filter((d) => d.verification_status === 'failed').length || 0,
      recentRequests: recentRequests?.length || 0,
      consentRecords: {
        total: consentRecords?.length || 0,
        granted: consentRecords?.filter((c) => c.consent_status === 'granted').length || 0,
        withdrawn: consentRecords?.filter((c) => c.consent_status === 'withdrawn').length || 0,
      },
      complianceScore: calculateGDPRScore(pendingDeletions, consentRecords),
    }

    return status
  } catch (error) {
    console.error('Error getting GDPR compliance status:', error)
    captureException(error, { context: 'get_gdpr_compliance_status' })
    throw error
  }
}

/**
 * Calculate GDPR compliance score
 */
const calculateGDPRScore = (pendingDeletions, consentRecords) => {
  let score = 100

  // Deduct for pending deletions
  if (pendingDeletions && pendingDeletions.length > 0) {
    score -= pendingDeletions.length * 10
  }

  // Deduct for missing consent records
  if (!consentRecords || consentRecords.length === 0) {
    score -= 20
  }

  return Math.max(0, Math.min(100, score))
}

/**
 * Get GDPR deletion verification history
 */
export const getGDPRDeletionHistory = async (organizationId, limit = 50) => {
  try {
    const { data, error } = await supabase
      .from('gdpr_deletion_verification')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false })
      .limit(limit)

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error fetching GDPR deletion history:', error)
    captureException(error, { context: 'get_gdpr_deletion_history' })
    throw error
  }
}

